import React, { useState, useEffect, useCallback } from "react";
import { apiClient, useAuth } from "../../App";
import { toast } from "sonner";
import {
  MessageSquare, Send, Reply, CheckCircle, RotateCcw,
  MoreHorizontal, Trash2, Pencil, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function CommentInput({ contractId, sectionId, sectionName, parentCommentId, onCommentAdded, placeholder, autoFocus, compact }) {
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      await apiClient.post(`/contracts/${contractId}/comments`, {
        section_id: sectionId,
        section_name: sectionName,
        content: text.trim(),
        parent_comment_id: parentCommentId || null,
      });
      setText("");
      onCommentAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={`flex gap-2 ${compact ? "" : "mt-2"}`} data-testid="comment-input">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={placeholder || "Add a comment... Use @name to mention someone"}
        className="rounded-sm text-sm min-h-[36px] resize-none"
        rows={compact ? 1 : 2}
        autoFocus={autoFocus}
        onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSubmit(); }}
        data-testid="comment-text-input"
      />
      <Button
        size="icon"
        onClick={handleSubmit}
        disabled={submitting || !text.trim()}
        className="rounded-sm h-9 w-9 shrink-0 bg-cyan-600 hover:bg-cyan-700"
        data-testid="comment-submit-btn"
      >
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
      </Button>
    </div>
  );
}

function CommentItem({ comment, contractId, currentUserId, isAdmin, onRefresh, depth = 0 }) {
  const [showReply, setShowReply] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(comment.content);
  const [saving, setSaving] = useState(false);
  const isOwn = comment.author_id === currentUserId;
  const isResolved = comment.status === "resolved";
  const isRoot = !comment.parent_comment_id;

  const handleEdit = async () => {
    if (!editText.trim()) return;
    setSaving(true);
    try {
      await apiClient.put(`/contracts/${contractId}/comments/${comment.id}`, { content: editText.trim() });
      setEditing(false);
      onRefresh();
    } catch { toast.error("Failed to update"); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await apiClient.delete(`/contracts/${contractId}/comments/${comment.id}`);
      toast.success("Comment deleted");
      onRefresh();
    } catch { toast.error("Failed to delete"); }
  };

  const handleResolve = async () => {
    try {
      await apiClient.post(`/contracts/${contractId}/comments/${comment.id}/resolve`);
      toast.success("Thread resolved");
      onRefresh();
    } catch { toast.error("Failed to resolve"); }
  };

  const handleReopen = async () => {
    try {
      await apiClient.post(`/contracts/${contractId}/comments/${comment.id}/reopen`);
      onRefresh();
    } catch { toast.error("Failed to reopen"); }
  };

  const roleColor = {
    superuser: "text-violet-600", admin: "text-violet-600", legal: "text-blue-600",
    procurement_manager: "text-emerald-600", finance: "text-amber-600",
    executive: "text-rose-600", contract_creator: "text-slate-600",
  };

  return (
    <div
      className={`${depth > 0 ? "ml-6 border-l-2 border-slate-100 dark:border-slate-800 pl-3" : ""} ${isResolved ? "opacity-60" : ""}`}
      data-testid={`comment-${comment.id}`}
    >
      <div className="py-2">
        <div className="flex items-center gap-2 text-xs">
          <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-[10px] font-semibold text-slate-600 dark:text-slate-300">
            {(comment.author_name || "?")[0].toUpperCase()}
          </div>
          <span className={`font-medium ${roleColor[comment.author_role] || "text-slate-700 dark:text-slate-300"}`}>
            {comment.author_name}
          </span>
          <span className="text-slate-400">{timeAgo(comment.created_at)}</span>
          {comment.updated_at && <span className="text-slate-300 italic">(edited)</span>}
          {isResolved && <Badge className="bg-emerald-100 text-emerald-700 text-[10px] h-4 px-1.5">Resolved</Badge>}
          {isRoot && comment.reply_count > 0 && (
            <Badge variant="outline" className="text-[10px] h-4 px-1.5 gap-0.5">
              <Reply size={9} /> {comment.reply_count}
            </Badge>
          )}
        </div>

        {editing ? (
          <div className="mt-1.5 flex gap-2">
            <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} className="rounded-sm text-sm min-h-[36px]" rows={2} data-testid="comment-edit-input" />
            <div className="flex flex-col gap-1">
              <Button size="sm" onClick={handleEdit} disabled={saving} className="h-7 text-xs rounded-sm">{saving ? <Loader2 size={12} className="animate-spin" /> : "Save"}</Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} className="h-7 text-xs rounded-sm">Cancel</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-700 dark:text-slate-300 mt-1 whitespace-pre-wrap leading-relaxed">
            {comment.content.split(/(@\w[\w\s]{1,30}?)(?=\s|$|[,.])/).map((part, i) =>
              part.startsWith("@") ? <span key={i} className="text-cyan-600 font-medium">{part}</span> : part
            )}
          </p>
        )}

        {/* Actions */}
        <div className="flex items-center gap-1 mt-1">
          {!isResolved && (
            <button onClick={() => setShowReply(!showReply)} className="text-xs text-slate-400 hover:text-cyan-600 flex items-center gap-1 transition-colors" data-testid={`reply-btn-${comment.id}`}>
              <Reply size={11} /> Reply
            </button>
          )}
          {isRoot && !isResolved && (isOwn || isAdmin) && (
            <button onClick={handleResolve} className="text-xs text-slate-400 hover:text-emerald-600 flex items-center gap-1 ml-2 transition-colors" data-testid={`resolve-btn-${comment.id}`}>
              <CheckCircle size={11} /> Resolve
            </button>
          )}
          {isRoot && isResolved && (isOwn || isAdmin) && (
            <button onClick={handleReopen} className="text-xs text-slate-400 hover:text-amber-600 flex items-center gap-1 ml-2 transition-colors" data-testid={`reopen-btn-${comment.id}`}>
              <RotateCcw size={11} /> Reopen
            </button>
          )}
          {(isOwn || isAdmin) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="text-xs text-slate-300 hover:text-slate-500 ml-2 transition-colors"><MoreHorizontal size={13} /></button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {isOwn && <DropdownMenuItem onClick={() => { setEditing(true); setEditText(comment.content); }}><Pencil size={12} className="mr-2" />Edit</DropdownMenuItem>}
                <DropdownMenuItem onClick={handleDelete} className="text-red-600"><Trash2 size={12} className="mr-2" />Delete</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {showReply && (
          <div className="mt-2">
            <CommentInput
              contractId={contractId}
              parentCommentId={comment.id}
              sectionId={comment.section_id}
              sectionName={comment.section_name}
              onCommentAdded={() => { setShowReply(false); onRefresh(); }}
              placeholder="Write a reply..."
              autoFocus
              compact
            />
          </div>
        )}
      </div>
    </div>
  );
}


export function CommentsPanel({ contractId, sections }) {
  const { user } = useAuth();
  const [comments, setComments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("all");
  const [showResolved, setShowResolved] = useState(false);

  const isAdmin = ["superuser", "admin"].includes(user?.role);

  const fetchComments = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (activeSection !== "all") params.append("section_id", activeSection);
      if (!showResolved) params.append("status", "open");
      const [commentsRes, summaryRes] = await Promise.all([
        apiClient.get(`/contracts/${contractId}/comments?${params}`),
        apiClient.get(`/contracts/${contractId}/comments/summary`),
      ]);
      setComments(commentsRes.data);
      setSummary(summaryRes.data);
    } catch {
      toast.error("Failed to load comments");
    } finally {
      setLoading(false);
    }
  }, [contractId, activeSection, showResolved]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  // Group into threads for display
  const threads = [];
  const threadMap = new Map();
  for (const c of comments) {
    if (!c.parent_comment_id) {
      const thread = { root: c, replies: [] };
      threadMap.set(c.thread_id, thread);
      threads.push(thread);
    } else {
      const thread = threadMap.get(c.thread_id);
      if (thread) thread.replies.push(c);
    }
  }

  const sectionOptions = [
    { id: "all", name: "All Sections" },
    ...(sections || []).map((s) => ({ id: s.id, name: s.name })),
    { id: "general", name: "General" },
  ];

  return (
    <div className="space-y-4" data-testid="comments-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageSquare size={16} className="text-cyan-600" />
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Comments</h3>
          {summary && (
            <div className="flex gap-2 text-[10px]">
              <Badge variant="outline" className="h-5 gap-1">{summary.open_threads} open</Badge>
              {summary.resolved_threads > 0 && (
                <Badge className="h-5 gap-1 bg-emerald-100 text-emerald-700">{summary.resolved_threads} resolved</Badge>
              )}
            </div>
          )}
        </div>
        <button
          onClick={() => setShowResolved(!showResolved)}
          className="text-xs text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-1"
          data-testid="toggle-resolved-btn"
        >
          {showResolved ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          {showResolved ? "Hide resolved" : "Show resolved"}
        </button>
      </div>

      {/* Section filter */}
      {sectionOptions.length > 2 && (
        <div className="flex gap-1 flex-wrap">
          {sectionOptions.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`px-2 py-0.5 text-[11px] rounded-sm border transition-colors ${
                activeSection === s.id
                  ? "bg-cyan-50 border-cyan-300 text-cyan-700 dark:bg-cyan-900/30 dark:border-cyan-700 dark:text-cyan-400"
                  : "border-slate-200 text-slate-500 hover:border-slate-300 dark:border-slate-700"
              }`}
              data-testid={`section-filter-${s.id}`}
            >
              {s.name}
              {summary?.by_section?.[s.id]?.open > 0 && (
                <span className="ml-1 text-cyan-600 font-medium">{summary.by_section[s.id].open}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* New comment input */}
      <CommentInput
        contractId={contractId}
        sectionId={activeSection !== "all" ? activeSection : null}
        sectionName={sectionOptions.find((s) => s.id === activeSection)?.name}
        onCommentAdded={fetchComments}
        placeholder="Start a new discussion..."
      />

      {/* Threads */}
      {loading ? (
        <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-slate-400" /></div>
      ) : threads.length > 0 ? (
        <div className="space-y-1 divide-y divide-slate-100 dark:divide-slate-800">
          {threads.map((thread) => (
            <div key={thread.root.id}>
              <CommentItem
                comment={thread.root}
                contractId={contractId}
                currentUserId={user?.id}
                isAdmin={isAdmin}
                onRefresh={fetchComments}
                depth={0}
              />
              {thread.replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  contractId={contractId}
                  currentUserId={user?.id}
                  isAdmin={isAdmin}
                  onRefresh={fetchComments}
                  depth={1}
                />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <MessageSquare size={28} className="mx-auto text-slate-200 mb-2" />
          <p className="text-sm text-slate-400">No comments yet</p>
          <p className="text-xs text-slate-300 mt-1">Start a discussion on this contract</p>
        </div>
      )}
    </div>
  );
}
